/* eslint-disable */
// src/components/DragAndDrop.tsx
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import FolderPickerButton from "./FolderPickerButton";
import DatabookStatusIndicator from "./DatabookStatusIndicator";
import {
  saveFolderRef,
  getFolderRef,
  clearFolderRef as storeClear,
  saveIndexMap,
  getIndexMap,
  saveIndexRows,
  getIndexRows,
  getIndexRowsTimestamp,
  saveProviderId,
  getProviderId,
  saveSyncTimestamp,
  getSyncTimestamp,
  pruneStaleData,
} from "../store/folderStore";
import { setupFolderStructureById } from "../utils/folderSetup";
import { fetchSharePointTree, extractCodesFromTree, buildChildrenCacheFromTree } from "../utils/sharePointTree";
import { routeAndUploadFiles } from "../utils/uploadRouter";
import { flattenItems } from "../utils/flattenDroppedItems";
import { fetchFolderMeta, countFoldersInLocation, resolveFolderLink } from "../utils/folderLookup";
import { sendUploadSummaryEmail } from "../utils/uploadAndEmail";
import { expandZipFiles } from "../utils/expandZipFiles";
import { VDR_PROVIDERS, type VDRProviderId, type IndexRow } from "../vdr/providers";
import { isProviderImplemented, parseWithProvider } from "../vdr/parserBridge";
import FilePreviewTable from "./FilePreviewTable";
import {
  markPendingUploadFailed,
  promotePendingUploadByFileName,
} from "../fdd/store/fddStore";
import FolderMirroring from "./FolderMirroring";
import FDDToolbox from "../fdd/components/FDDToolbox";
import TaxToolbox from "../tax/components/TaxToolbox";
import { apiFetch } from "../api/fddClient";
import { useDemoView } from "../theme/demoView";
import IRLToolbox from "../irl/components/IRLToolbox";
import PPTToolbox from "../ppt/components/PPTToolbox";
import { downloadFilesAsZip } from "../utils/downloadFiles";
import { invalidateSharePointTreeCache } from "../utils/sharePointSearch";

type MockStatus = "queued" | "uploading" | "done" | "error" | "skipped";
type Item = { name: string; status: MockStatus; progress?: number; note?: string };

type Project = {
  id: string;
  name: string;
  updatedAt: string;
  folder?: {
    driveId: string;
    folderId: string;
    displayPath: string; // plain text shown in UI
    webUrl?: string;     // stored for future, not rendered
  };
};

type PickedFolder = { id: string; driveId: string; webUrl: string; name: string };

const LS_KEY = "projects_v1";

function loadProjects(): Project[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as Project[]) : [];
  } catch {
    return [];
  }
}

function saveProjects(list: Project[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(list));
}

function mkDisplayPath(f: PickedFolder): string {
  try {
    const u = new URL(f.webUrl);
    return `${f.name} — ${u.host}${u.pathname}`;
  } catch {
    return f.name;
  }
}

const badge = (ok: boolean, label: string) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      background: ok ? "var(--theme-tint-21)" : "#f0f0f0",
      color: ok ? "var(--theme-ink)" : "#666",
      border: `1px solid ${ok ? "var(--theme-border)" : "#ddd"}`,
    }}
  >
    <span style={{ fontSize: 12 }}>{ok ? "✅" : "•"}</span>
    {label}
  </span>
);

const DragAndDrop: React.FC = () => {
  const { id } = useParams(); // /project/:id
  const [projects, setProjects] = useState<Project[]>(() => loadProjects());
  const [project, setProject] = useState<Project | null>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [items, setItems] = useState<Item[]>([]);
  const [uploading, setUploading] = useState(false);

  // running flag for index creation
  const [indexRunning, setIndexRunning] = useState(false);
  // derived flag: do we have an index map for this project?
  const [hasIndex, setHasIndex] = useState<boolean>(false);

  const [messages, setMessages] = useState<string[]>([]);
  // Paste-a-link fallback for when the OneDrive picker popup can't report back
  const [folderLink, setFolderLink] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const indexInputRef = useRef<HTMLInputElement | null>(null);
  const spChildrenCacheRef = useRef<Map<string, { id: string; name: string; folder?: any }[]> | null>(null);

  // ✅ NEW: provider selection state (default to "basic")
  const [providerId, setProviderId] = useState<VDRProviderId>("none");

  // SharePoint sync state
  const [syncRunning, setSyncRunning] = useState(false);
  const [lastSyncDate, setLastSyncDate] = useState<string | null>(null);

  // Mode toggles: top-level (VDR Management / FDD Toolbox / Tax Toolbox / IRL) and within VDR (Dataroom vs Mirroring)
  const [topMode, setTopMode] = useState<"vdr" | "fdd" | "tax" | "irl" | "ppt">("vdr");
  const [vdrMode, setVdrMode] = useState<"dataroom" | "mirroring">("dataroom");

  // The Tax Toolbox is shown only to the users server/tax/access.ts lists; the
  // server says whether this user is one of them. Hidden until it answers yes.
  const [taxEnabled, setTaxEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    apiFetch("/api/tax/enabled", {}, { interactive: false })
      .then((res) => (res.ok ? res.json() : { enabled: false }))
      .then((body: { enabled?: boolean }) => {
        if (live) setTaxEnabled(body.enabled === true);
      })
      .catch(() => {
        /* signed out or unreachable: the tab stays hidden */
      });
    return () => {
      live = false;
    };
  }, []);

  // The Leo Berwick view (demo) hides the Tax Toolbox even for listed users.
  const lbView = useDemoView();
  const showTax = taxEnabled && !lbView;
  useEffect(() => {
    if (!showTax && topMode === "tax") setTopMode("vdr");
  }, [showTax, topMode]);

  // Large project: prior index rows (uploaded by user, stored in memory only)
  const [priorIndexRows, setPriorIndexRows] = useState<IndexRow[] | null>(null);
  const priorIndexInputRef = useRef<HTMLInputElement | null>(null);

  // Flagged files: files that couldn't be routed (no parent folder in map)
  const [flaggedFiles, setFlaggedFiles] = useState<File[]>([]);

  // Folder creation confirmation state
  const [folderPreview, setFolderPreview] = useState<{
    file: File;
    totalFolders: number;
    knownFromPrevious: number;
    willReuse: number;
    willCreate: number;
    driveId: string;
    folderId: string;
    allRows: IndexRow[];
    allParsedRows: IndexRow[]; // includes documents — needed for Firmex file routing
    newRows: IndexRow[];
    rowsToCreate: IndexRow[];
    dryRunMap: Record<string, string>;
  } | null>(null);

  // Load the current project by :id
  useEffect(() => {
    const list = loadProjects();
    setProjects(list);
    const found = list.find((p) => p.id === id);
    setProject(found ?? null);
  }, [id]);

  // Track whether an index map/rows already exist for this project (survives refresh)
  useEffect(() => {
    if (!project?.id) {
      setHasIndex(false);
      setLastSyncDate(null);
      return;
    }
    const map = getIndexMap(project.id) || {};
    const hasMap = Object.keys(map).length > 0;

    // Check for stored index rows + timestamp
    const ts = getIndexRowsTimestamp(project.id);
    const rows = getIndexRows(project.id);
    const hasRows = !!(ts && rows && rows.length > 0);

    // hasIndex = true if we have either a map OR stored rows (large projects only store rows)
    setHasIndex(hasMap || hasRows);

    // Restore sync timestamp
    const syncTs = getSyncTimestamp(project.id);
    setLastSyncDate(syncTs);

    // Restore the VDR provider used when the index was created
    const savedProvider = getProviderId(project.id);
    if (savedProvider) {
      setProviderId(savedProvider);
    }
  }, [project?.id]);

  // If the project has no folder yet, but we have one in folderStore, hydrate it
  useEffect(() => {
    if (!project?.id || project.folder) return;

    const saved = getFolderRef(project.id);
    if (!saved) return;

    let displayPath = saved.name || "";
    try {
      if (saved.webUrl) {
        const url = new URL(saved.webUrl);
        displayPath = `${saved.name || ""} — ${url.host}${url.pathname}`;
      }
    } catch {}

    setProject((prev) => {
      if (!prev || prev.id !== project.id) return prev;
      const patch: Project = {
        ...prev,
        updatedAt: new Date().toISOString(),
        folder: {
          driveId: saved.driveId,
          folderId: saved.itemId,
          displayPath: displayPath || saved.name || "",
          webUrl: saved.webUrl,
        },
      };
      setProjects((prevList) => {
        const next = prevList
          .map((p) => (p.id === patch.id ? patch : p))
          .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
        saveProjects(next);
        return next;
      });
      setMessages((prevMsgs) => [...prevMsgs, "🔄 Restored previously selected folder for this project."]);
      return patch;
    });
  }, [project?.id, project?.folder]);

  // Persist project updates back into the list
  const updateProject = (updater: (p: Project) => Project) => {
    setProject((prev) => {
      if (!prev) return prev;
      const updated = updater(prev);
      setProjects((prevList) => {
        const next = prevList
          .map((p) => (p.id === updated.id ? updated : p))
          .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
        saveProjects(next);
        return next;
      });
      return updated;
    });
  };

  // ----- File handling -----

  // Request-list VDRs file one document under several requests, so their ZIP holds
  // several same-named copies. Upload sends a file to every request the index lists
  // it under, and this list is keyed by name — so keep one copy of each.
  const dropDuplicateNames = (incoming: File[]): File[] => {
    if (providerId !== "requestlist") return incoming;
    const seen = new Set(files.map((f) => f.name.toLowerCase()));
    const kept = incoming.filter((f) => {
      const key = f.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const merged = incoming.length - kept.length;
    if (merged > 0) {
      setMessages((prev) => [...prev, `📑 Merged ${merged} duplicate cop${merged === 1 ? "y" : "ies"} — each file goes to every folder the index lists it under.`]);
    }
    return kept;
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const items = e.dataTransfer?.items && e.dataTransfer.items.length ? e.dataTransfer.items : e.dataTransfer?.files;
    if (!items) return;

    let droppedFiles: File[] = [];
    try {
      droppedFiles = await flattenItems(items as any);
    } catch (err) {
      // fallback to simple files list
      droppedFiles = Array.from(e.dataTransfer.files || [] as File[]);
    }

    // Expand any ZIP files to extract their contents
    try {
      droppedFiles = await expandZipFiles(droppedFiles);
    } catch (err: any) {
      setMessages((prev) => [...prev, `❌ ${err?.message ?? String(err)}`]);
      return;
    }
    droppedFiles = dropDuplicateNames(droppedFiles);

    if (droppedFiles.length === 0) return;
    setFiles((prev) => [...prev, ...droppedFiles]);
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      droppedFiles.forEach(f => next.add(f.name));
      return next;
    });
    setItems((prev) => [
      ...prev,
      ...droppedFiles.map((f) => ({ name: f.name, status: "queued" as MockStatus, progress: 0 })),
    ]);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
  // Capture the input BEFORE any await so it doesn't get nulled by React's pooling
  const input = e.currentTarget;

  const selected = input.files ? Array.from(input.files) : [];
  if (selected.length === 0) return;

  let filesToAdd: File[] = [];
  try {
    filesToAdd = await flattenItems(selected);
  } catch (err) {
    filesToAdd = selected;
  }

  // Expand any ZIP files to extract their contents
  try {
    filesToAdd = await expandZipFiles(filesToAdd);
  } catch (err: any) {
    setMessages((prev) => [...prev, `❌ ${err?.message ?? String(err)}`]);
    input.value = "";
    return;
  }
  filesToAdd = dropDuplicateNames(filesToAdd);

  setFiles((prev) => [...prev, ...filesToAdd]);
  setSelectedFiles((prev) => {
    const next = new Set(prev);
    filesToAdd.forEach(f => next.add(f.name));
    return next;
  });
  setItems((prev) => [
    ...prev,
    ...filesToAdd.map((f) => ({ name: f.name, status: "queued" as MockStatus, progress: 0 })),
  ]);

  // Now we use the captured DOM node, not e.currentTarget
  input.value = "";
};

  // ----- File selection toggle functions -----
  const toggleFileSelection = (fileName: string) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(fileName)) {
        next.delete(fileName);
      } else {
        next.add(fileName);
      }
      return next;
    });
  };

  const toggleAllFiles = () => {
    if (selectedFiles.size === files.length) {
      // Deselect all
      setSelectedFiles(new Set());
    } else {
      // Select all
      setSelectedFiles(new Set(files.map(f => f.name)));
    }
  };

  const downloadDeselected = async () => {
    const deselectedFiles = files.filter(f => !selectedFiles.has(f.name));
    if (deselectedFiles.length === 0) {
      setMessages((prev) => [...prev, "ℹ️ No deselected files to download."]);
      return;
    }

    try {
      await downloadFilesAsZip(deselectedFiles, "deselected-files.zip");
      setMessages((prev) => [
        ...prev,
        `✅ Downloaded ${deselectedFiles.length} deselected file(s) as ZIP.`,
      ]);
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        `❌ Failed to download files: ${err?.message || String(err)}`,
      ]);
    }
  };

  // ----- Large project: upload prior index (baseline for comparison) -----
  const handlePriorIndexUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIndexRunning(true);
      setMessages((prev) => [...prev, "🔍 Parsing prior index file..."]);

      const allParsedRows = await parseWithProvider(providerId, file);
      const folderRows = allParsedRows.filter(r => !r.meta?.isDocument);

      setPriorIndexRows(folderRows);
      setHasIndex(true);
      setMessages((prev) => [
        ...prev,
        `✅ Prior index loaded (${folderRows.length} folders). Now upload the new index to compare.`,
      ]);
    } catch (err: any) {
      setMessages((prev) => [...prev, `❌ Failed to parse prior index: ${err?.message ?? String(err)}`]);
    } finally {
      setIndexRunning(false);
      if (priorIndexInputRef.current) priorIndexInputRef.current.value = "";
    }
  };

  // ----- Parse index file and show preview (dry-run) -----
  // ----- Sync folder structure from SharePoint -----
  const handleSyncFromSharePoint = async () => {
    const folder = project?.folder;
    if (!folder?.driveId || !folder.folderId) {
      setMessages((prev) => [...prev, "❌ Choose a folder first."]);
      return;
    }
    await syncFolderStructure({ driveId: folder.driveId, folderId: folder.folderId });
  };

  // Takes the folder explicitly so it can run right after picking, before the
  // project-state update has propagated back through React.
  const syncFolderStructure = async (folder: { driveId: string; folderId: string }) => {
    setSyncRunning(true);
    setMessages((prev) => [...prev, "🔄 Full sync: reading folder structure from SharePoint..."]);

    try {
      const tree = await fetchSharePointTree(
        folder.driveId,
        folder.folderId,
        (count) => {
          // Update progress in the last message
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = `🔄 Full sync: ${count} folders found...`;
            return copy;
          });
        }
      );

      const savedRows = project?.id ? (getIndexRows(project.id) ?? []) : [];
      const useFilerooms = savedRows.filter(r => r.meta?.isFileRoom).length > 1;
      spChildrenCacheRef.current = buildChildrenCacheFromTree(tree);
      const spMap = extractCodesFromTree(tree, providerId, useFilerooms, savedRows.length > 0 ? savedRows : undefined);
      const folderCount = Object.keys(spMap).length;

      // Save to localStorage
      if (project?.id) {
        pruneStaleData(projects.map(p => p.id));
        try {
          saveIndexMap(project.id, spMap);
        } catch (storageErr: any) {
          setMessages((prev) => [...prev, `⚠️ ${storageErr.message}`]);
        }
        saveSyncTimestamp(project.id, new Date().toISOString());
        setLastSyncDate(new Date().toISOString());
        setHasIndex(folderCount > 0);
      }

      setMessages((prev) => [...prev, `✅ Full sync complete: ${folderCount} folders mapped.`]);
    } catch (err: any) {
      setMessages((prev) => [...prev, `❌ Sync failed: ${err?.message || String(err)}`]);
    } finally {
      setSyncRunning(false);
    }
  };

  const handleIndexFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const indexFile = e.target.files?.[0];
    if (!indexFile) return;

    try {
      const folder = project?.folder;
      if (!folder?.driveId || !folder.folderId) {
        setMessages((prev) => [...prev, "❌ Choose a folder first (top box)."]);
        return;
      }

      setIndexRunning(true);
      setMessages((prev) => [...prev, "🔍 Analyzing index file..."]);

      // 1. Parse the index file once
      const allParsedRows = await parseWithProvider(providerId, indexFile);

      // 2. Filter to FOLDERS only (exclude documents marked with meta.isDocument)
      const allRows = allParsedRows.filter(r => !r.meta?.isDocument);

      // Compare against the synced SharePoint index map
      let spMap = project?.id ? (getIndexMap(project.id) ?? {}) : {};

      // Request-list VDR: folder names start with colliding numbers, so a map from a
      // code-based sync can point at the wrong folders. Re-read the tree and match
      // folders to this index by exact name instead.
      if (providerId === "requestlist") {
        setMessages((prev) => [...prev, "🔄 Matching SharePoint folders to the index..."]);
        const tree = await fetchSharePointTree(folder.driveId, folder.folderId);
        spChildrenCacheRef.current = buildChildrenCacheFromTree(tree);
        spMap = extractCodesFromTree(tree, "requestlist", false, allRows);
        if (project?.id) saveIndexMap(project.id, spMap);
      }

      const hasSpMap = Object.keys(spMap).length > 0;

      if (!hasSpMap && providerId !== "requestlist") {
        setMessages((prev) => [
          ...prev,
          "⚠️ No SharePoint sync found. Click 'Sync from SharePoint' first, or proceeding will create all folders.",
        ]);
      }

      // Firmex: SP folders may have been created with original titles (no number prefix).
      // If so, the cached spMap is missing scoped subfolder keys. Detect this and rebuild
      // with title-based matching using the freshly parsed index rows.
      if (providerId === "firmex" && hasSpMap && allRows.length > 0) {
        const rootRows = allRows.filter(r => !r.code.includes(".") && !r.meta?.isDocument);
        const needsRebuild = rootRows.some(root => {
          const rootKey = `__root:${root.title}`;
          if (!spMap[rootKey]) return false; // root not in SP or numbered root
          const firstChild = allRows.find(r =>
            !r.meta?.isDocument &&
            r.code.startsWith(root.code + ".") &&
            r.code.split(".").length === root.code.split(".").length + 1
          );
          if (!firstChild) return false;
          const relCode = firstChild.code.slice(root.code.length + 1);
          return !spMap[`${rootKey}::${relCode}`] && !spMap[firstChild.code];
        });

        console.log(`[firmex-debug] needsRebuild=${needsRebuild} spMapKeys(sample)=${JSON.stringify(Object.keys(spMap).slice(0, 6))}`);
        if (needsRebuild) {
          setMessages((prev) => [...prev, "🔄 Verifying SharePoint folder structure..."]);
          try {
            const tree = await fetchSharePointTree(folder.driveId, folder.folderId);
            spChildrenCacheRef.current = buildChildrenCacheFromTree(tree);
            spMap = extractCodesFromTree(tree, "firmex", false, allRows);
            console.log(`[firmex-debug] After rebuild: ${Object.keys(spMap).length} keys, sample=${JSON.stringify(Object.keys(spMap).slice(0, 8))}`);
            if (project?.id) {
              saveIndexMap(project.id, spMap);
              saveSyncTimestamp(project.id, new Date().toISOString());
              setLastSyncDate(new Date().toISOString());
            }
          } catch (err: any) {
            console.warn("[firmex] Re-sync for title matching failed:", err?.message);
          }
        }
      }

      // Local comparison: which VDR codes already exist in SharePoint?
      const dryRunMap: Record<string, string> = {};
      const rowsToCreate: IndexRow[] = [];
      let willReuse = 0;
      let willCreate = 0;

      // Scoped keys are only used when there are 2+ filerooms.
      // A single fileroom is treated as standard (no __fr: prefixes).
      const fileRoomCount = allParsedRows.filter(r => r.meta?.isFileRoom).length;
      const useFileromsForPreview = fileRoomCount > 1;

      // Firmex: index uses sequential root codes ("1", "2") but SP map uses either:
      //   plain keys ("1") for numbered roots, or scoped keys ("__root:R2 VDR::14") for unnumbered roots.
      // Detect unnumbered roots by checking if spMap has a "__root:Title" entry for that root.
      const firmexToSpKey = providerId === "firmex"
        ? (() => {
            const rootRows = allRows.filter(r => !r.code.includes(".") && !r.meta?.isDocument);
            return (rowCode: string): string => {
              const segs = rowCode.split(".");
              const root = rootRows.find(r => r.code === segs[0]);
              if (!root) return rowCode;
              const rootKey = `__root:${root.title}`;
              const relCode = segs.slice(1).join(".");
              const scopedKey = relCode ? `${rootKey}::${relCode}` : rootKey;
              return spMap[scopedKey] !== undefined ? scopedKey : rowCode;
            };
          })()
        : null;

      for (const row of allRows) {
        // FILEROOM rows are only relevant when 2+ filerooms exist.
        // In single-fileroom mode they are not created as folders — skip them.
        if (row.meta?.isFileRoom && !useFileromsForPreview) continue;

        // Build the same scoped key that extractDataroomCodes / createFoldersFromIndexById uses.
        // FILEROOM rows (code="__fileroom") → "__fr:Title"
        // Rows inside a fileroom          → "__fr:FileroomName::code"
        // Plain rows (no fileroom)        → plain code
        const frName = useFileromsForPreview ? (row.meta?.rootFolderName as string | undefined) : undefined;
        const spKey = firmexToSpKey
          ? firmexToSpKey(row.code)
          : (useFileromsForPreview && row.meta?.isFileRoom)
          ? `__fr:${row.title}`
          : frName
          ? `__fr:${frName}::${row.code}`
          : row.code;

        if (spMap[spKey]) {
          // Folder exists in SharePoint.
          // Store under the scoped key (for ZIP-based routing via resolveFirmexFolderByZipPath)
          // AND under the plain row code (for path-based routing via resolveFirmexFileByPath).
          dryRunMap[spKey] = spMap[spKey];
          if (firmexToSpKey && spKey !== row.code) {
            dryRunMap[row.code] = spMap[spKey];
          }
          willReuse++;
        } else {
          // Folder needs creating
          rowsToCreate.push(row);
          willCreate++;
        }
      }

      const knownFromPrevious = willReuse;

      if (willCreate > 0) {
        setMessages((prev) => [...prev, `📊 Found ${willCreate} new folders to create, ${willReuse} already exist.`]);
      } else {
        setMessages((prev) => [...prev, "✅ All folders already exist in SharePoint."]);
      }

      // Show preview with all counts
      setFolderPreview({
        file: indexFile,
        totalFolders: allRows.length,
        knownFromPrevious,
        willReuse,
        willCreate,
        driveId: folder.driveId,
        folderId: folder.folderId,
        allRows,
        allParsedRows, // includes documents — needed for Firmex routing
        newRows: rowsToCreate,
        rowsToCreate,
        dryRunMap,
      });

      setMessages((prev) => [...prev, "📊 Preview ready. Review counts below."]);
    } catch (err: any) {
      setMessages((prev) => [...prev, `❌ Failed to analyze index: ${err?.message ?? String(err)}`]);
    } finally {
      setIndexRunning(false);
      if (indexInputRef.current) indexInputRef.current.value = "";
    }
  };

  // ----- Actually create folders after confirmation -----
  const confirmAndCreateFolders = async () => {
    if (!folderPreview) return;

    try {
      const folder = project?.folder;
      if (!folder?.driveId || !folder.folderId) {
        setMessages((prev) => [...prev, "❌ Folder reference lost."]);
        return;
      }

      const providerLabel = VDR_PROVIDERS.find(p => p.id === providerId)?.label ?? providerId;

      setIndexRunning(true);
      setFolderPreview(null); // Clear preview

      // For Firmex the targeted flow passes allRows (rowsToCreate is empty by design)
      const hasFoldersToProcess = providerId === "firmex"
        ? folderPreview.allRows.length > 0
        : folderPreview.rowsToCreate.length > 0;

      if (hasFoldersToProcess) {
        setMessages((prev) => [
          ...prev,
          `🚀 Creating ${folderPreview.willCreate} folders from index (${providerLabel}): "${folderPreview.file.name}" → ${folder.displayPath || "selected folder"}`,
        ]);

        // Merge stored index map + dry-run map so ancestors resolve without API calls
        const storedMap = project?.id ? (getIndexMap(project.id) ?? {}) : {};
        const existingMap = { ...storedMap, ...folderPreview.dryRunMap };

        // Firmex creation function doesn't support existingMap — it needs ALL rows
        // (including existing ancestors) so parent resolution works. It reuses existing folders.
        const rowsForCreation = providerId === "firmex"
          ? folderPreview.allRows
          : folderPreview.rowsToCreate;

        const res = await setupFolderStructureById(
          null, // no file needed - we pass pre-parsed rows
          folder.driveId,
          folder.folderId,
          providerId,
          false, // dryRun = false (actually create folders)
          rowsForCreation,
          existingMap, // pre-seed map with all existing folder IDs (used by non-Firmex)
          spChildrenCacheRef.current ?? undefined
        );

        const summary = `✅ Created: ${res.created} • Reused: ${res.reused} • Skipped: ${res.skipped}`;
        setMessages((prev) => [...prev, summary]);

        // Save the map from creation (includes new + ancestor folders)
        if (project?.id) {
          const priorMap = getIndexMap(project.id) ?? {};
          const mergedMap = { ...priorMap, ...folderPreview.dryRunMap, ...res.map };
          saveIndexMap(project.id, mergedMap);
          setHasIndex(Object.keys(mergedMap).length > 0);
        }

        if (res.warnings?.length) {
          setMessages((prev) => [...prev, ...res.warnings.map((w) => `⚠️ ${w}`)]);
        }
      } else {
        setMessages((prev) => [...prev, "✅ No new folders to create."]);

        // Still save the dryRunMap so subfolder codes are available for file routing
        if (project?.id) {
          const priorMap = getIndexMap(project.id) ?? {};
          const mergedMap = { ...priorMap, ...folderPreview.dryRunMap };
          saveIndexMap(project.id, mergedMap);
          setHasIndex(Object.keys(mergedMap).length > 0);
        }
      }

      // Save ALL rows including documents (for future comparisons + Firmex file routing)
      if (project?.id) {
        try {
          saveIndexRows(project.id, folderPreview.allParsedRows);
        } catch (rowsErr: any) {
          setMessages((prev) => [...prev, `⚠️ ${rowsErr.message}`]);
        }
        saveProviderId(project.id, providerId);
        setMessages((prev) => [...prev, "📌 Saved index for future comparisons."]);
      }

    } catch (err: any) {
      setMessages((prev) => [...prev, `💥 Failed to create folders: ${err?.message ?? String(err)}`]);
    } finally {
      setIndexRunning(false);
    }
  };

  // ----- Cancel folder creation -----
  const cancelFolderCreation = () => {
    setFolderPreview(null);
    setMessages((prev) => [...prev, "❌ Folder creation cancelled."]);
  };

  // ----- Folder picker wiring -----
  const onPicked = async (picked: PickedFolder) => {
    // 1) Fetch authoritative name from Graph
    const meta = await fetchFolderMeta(picked.driveId, picked.id);

    // 2) Build a display string with the confirmed name
    const confirmed: PickedFolder = {
      ...picked,
      name: meta.name || picked.name,         // prefer Graph name
      webUrl: meta.webUrl || picked.webUrl,   // carry over if Graph omits
    };
    const display = mkDisplayPath(confirmed);

    // 3) Update project state
    updateProject((p) => ({
      ...p,
      updatedAt: new Date().toISOString(),
      folder: {
        driveId: picked.driveId,
        folderId: picked.id,
        displayPath: display,
        webUrl: confirmed.webUrl,
      },
    }));

    // 4) Persist for later use (store the confirmed name)
    if (project?.id) {
      saveFolderRef(project.id, {
        driveId: picked.driveId,
        itemId: picked.id,
        webUrl: confirmed.webUrl,
        name: confirmed.name,
      });
    }

    // 5) Log with the confirmed name (no “undefined”)
    setMessages((prev) => [...prev, `📁 Selected folder: ${confirmed.name}`]);

    // 6) Picking a folder alone never writes the index map the IRL tab reads —
    //    if none exists yet for this project, sync the structure now.
    if (project?.id && Object.keys(getIndexMap(project.id) || {}).length === 0) {
      void syncFolderStructure({ driveId: picked.driveId, folderId: picked.id });
    }
  };

  const applyFolderLink = async () => {
    if (!folderLink.trim() || linkBusy) return;
    setLinkError(null);
    setLinkBusy(true);
    try {
      await onPicked(await resolveFolderLink(folderLink));
      setFolderLink("");
    } catch (err: any) {
      setLinkError(err?.message || "Couldn't use that link.");
    } finally {
      setLinkBusy(false);
    }
  };

  const clearFolder = () => {
    if (!project?.folder) return;
    updateProject((p) => ({
      ...p,
      folder: undefined,
      updatedAt: new Date().toISOString(),
    }));
    if (project?.id) {
      storeClear(project.id);
    }
    setMessages((prev) => [...prev, "🗑️ Cleared selected folder."]);
  };

  // ----- REAL upload using router (sequential with progress) -----
  const uploadFiles = async () => {
    const folder = project?.folder; // narrow once
    if (!folder?.driveId || !folder.folderId) {
      setMessages((prev) => [...prev, "❌ Choose a folder first."]);
      return;
    }
    if (files.length === 0) {
      setMessages((prev) => [...prev, "❌ Add files to upload."]);
      return;
    }

    const driveId = folder.driveId;
    const rootItemId = folder.folderId;

    // Strict policy: require an index map to route
    const indexMap = project?.id ? (getIndexMap(project.id) || {}) : {};
    if (!indexMap || Object.keys(indexMap).length === 0) {
      setMessages((prev) => [...prev, "❌ No index map found. Create folders from index first."]);
      return;
    }

    // Get index rows for provider-specific routing (e.g., Firmex)
    const indexRows = project?.id ? (getIndexRows(project.id) || []) : [];

    // Filter to only selected files
    const filesToUpload = files.filter(f => selectedFiles.has(f.name));
    if (filesToUpload.length === 0) {
      setMessages((prev) => [...prev, "❌ No files selected for upload."]);
      return;
    }

    setUploading(true);

    // Files were already unzipped when added (drop/picker), so don't unzip again
    // here — a second pass would re-open nested ZIP documents. Unzip once.
    const expandedFiles = filesToUpload;

    setMessages((prev) => [
      ...prev,
      `🚀 Uploading ${expandedFiles.length} file(s) to ${folder.displayPath || "selected folder"} (using index routing)…`,
    ]);

    const results = await routeAndUploadFiles({
      driveId,
      rootItemId,
      files: expandedFiles,
      indexMap,
      providerId,
      indexRows,
      onProgress: (file, p) => {
        setItems((prev) =>
          prev.map((it) =>
            it.name === file.name ? { ...it, progress: Math.round(p * 100) } : it
          )
        );
      },
      onStatus: (file, status, note) => {
        setItems((prev) =>
          prev.map((it) =>
            it.name === file.name
              ? {
                  ...it,
                  status,
                  note: note ?? it.note,
                  progress:
                    status === "uploading" ? it.progress ?? 0 : status === "done" ? 100 : it.progress,
                }
              : it
          )
        );
      },
    });

    const ok = (results as any[]).filter((r: any) => r.status === "done").length;
    const skipped = (results as any[]).filter((r: any) => r.status === "skipped").length;
    const bad = (results as any[]).filter((r: any) => r.status === "error").length;

    // Promote any FDD queue items the user tagged in the preview table — they
    // were sitting at "pending_upload" until SharePoint refs were known.
    //
    // Two paths produce a usable file in SharePoint:
    //   1. status === "done"     — we just uploaded it.
    //   2. status === "skipped"  — it was already there as a duplicate;
    //                              the upload router now surfaces the
    //                              EXISTING file's refs so we can promote
    //                              the FDD queue item the same way.
    // Either way, if we have driveId + itemId, the FDD tools can fetch
    // the file via Graph and run analysis. No re-upload needed.
    if (project?.id) {
      // A request-list file can return one result per target folder. Promote and
      // mark-failed only act on items still pending, so handle usable copies first:
      // one copy in SharePoint is enough for the FDD tools, even if another failed.
      const usable = (r: any) => (r.status === "done" || r.status === "skipped") && r.driveId && r.itemId ? 1 : 0;
      for (const r of [...(results as any[])].sort((a, b) => usable(b) - usable(a))) {
        const hasRefs = r.driveId && r.itemId;
        if ((r.status === "done" || r.status === "skipped") && hasRefs) {
          promotePendingUploadByFileName(project.id, r.fileName, {
            driveId: r.driveId,
            fileId: r.itemId,
            fileWebUrl: r.webUrl ?? "",
            fileSize: r.fileSize,
          });
        } else if (r.status === "error") {
          markPendingUploadFailed(project.id, r.fileName, r.message || "Upload failed");
        } else {
          // Edge case: skipped without refs (Graph fetch failed). Mark as
          // an explicit error so the user removes + re-queues via the SP
          // picker instead of silently sitting in pending_upload forever.
          markPendingUploadFailed(
            project.id,
            r.fileName,
            r.message || "Upload skipped and existing file refs unavailable — re-queue from the FDD SharePoint picker.",
          );
        }
      }
      // Drop any cached tree for this project — the FDD run-time file
      // resolver walks the SP tree to find queued files by name. After
      // an upload, that cache is stale; the next FDD Run should refetch.
      if (project?.folder?.driveId && project?.folder?.folderId) {
        invalidateSharePointTreeCache(project.folder.driveId, project.folder.folderId);
      }
    }

    // Flag failed files for ZIP download
    const failedFiles = expandedFiles.filter(f =>
      results.some((r: any) => r.fileName === f.name && r.status === "error")
    );
    if (failedFiles.length > 0) {
      setFlaggedFiles((prev) => [...prev, ...failedFiles]);
    }

    // Save any newly discovered folder IDs for future uploads
    if (project?.id) {
      saveIndexMap(project.id, indexMap);
    }

    setMessages((prev) => [
      ...prev,
      `✅ Finished uploading. Success: ${ok} • Skipped: ${skipped} • Failed: ${bad}`,
    ]);

    setUploading(false);
  };

  // ----- Render -----
  if (!project) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "var(--theme-tint-3)",
          fontFamily: "Arial",
          padding: "32px 20px",
          display: "grid",
          placeItems: "center",
          color: "var(--theme-ink)",
        }}
      >
        Project not found.
      </div>
    );
  }

  const folderSelected = !!(project.folder?.driveId && project.folder?.folderId);

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--theme-tint-3)",
        fontFamily: "Arial",
        padding: "32px 20px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          background: "#ffffff",
          border: "1px solid var(--theme-border-strong)",
          borderRadius: 12,
          padding: 24,
          boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, color: "var(--theme-ink)" }}>{project.name}</h2>
          <div style={{ fontSize: 12, color: "var(--theme-ink-soft)" }}>Signed in • Demo mode</div>
        </div>

        {/* Folder selector */}
        <div
          style={{
            marginTop: 16,
            border: "1px solid var(--theme-border)",
            background: "var(--theme-tint-15)",
            borderRadius: 12,
            padding: 12,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <FolderPickerButton label={project.folder ? "Change folder" : "Choose folder"} onPicked={onPicked} />

          <div style={{ fontSize: 12, color: "var(--theme-ink)" }}>
            {folderSelected ? (
              <span title={project.folder?.webUrl || project.folder?.displayPath}>
                <b>Folder:</b> {project.folder!.displayPath}
              </span>
            ) : (
              <span>No folder selected</span>
            )}
          </div>

          {folderSelected && (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={clearFolder}
                style={{
                  background: "#ffffff",
                  color: "var(--theme-ink)",
                  border: "1px solid var(--theme-border)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                Clear
              </button>
            </div>
          )}

          {/* Lightweight status badges */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {badge(folderSelected, "Folder selected")}
            {badge(hasIndex, "Index loaded")}
          </div>

          {/* Paste-a-link fallback, on its own row */}
          <div style={{ flexBasis: "100%", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--theme-ink)" }}>or paste a folder link:</span>
            <input
              type="text"
              value={folderLink}
              onChange={(e) => setFolderLink(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFolderLink()}
              placeholder="Open the folder in SharePoint → Copy link → paste here"
              disabled={linkBusy}
              style={{ flex: 1, minWidth: 240, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--theme-border)", fontSize: 12 }}
            />
            <button
              onClick={applyFolderLink}
              disabled={linkBusy || !folderLink.trim()}
              style={{
                background: "#ffffff",
                color: "var(--theme-ink)",
                border: "1px solid var(--theme-border)",
                borderRadius: 8,
                padding: "8px 10px",
                cursor: linkBusy || !folderLink.trim() ? "default" : "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {linkBusy ? "Checking…" : "Use link"}
            </button>
            {linkError && <span style={{ flexBasis: "100%", fontSize: 12, color: "#b3261e" }}>{linkError}</span>}
          </div>
        </div>

        {/* Canonical-store status — always visible across every tab so the
            analyst knows which databook is the source of truth. */}
        <DatabookStatusIndicator projectId={project.id} />

        {/* Top-level mode toggle: VDR Management / FDD Toolbox / IRL */}
        <div
          style={{
            display: "flex",
            gap: 0,
            marginTop: 16,
            borderRadius: topMode === "vdr" ? "10px 10px 0 0" : 10,
            overflow: "hidden",
            border: "1px solid var(--theme-border-strong)",
            borderBottom: topMode === "vdr" ? "none" : "1px solid var(--theme-border-strong)",
          }}
        >
          <button
            onClick={() => setTopMode("vdr")}
            style={{
              flex: 1,
              background: topMode === "vdr" ? "var(--theme-ink)" : "var(--theme-tint-7)",
              color: topMode === "vdr" ? "#fff" : "var(--theme-ink)",
              border: "none",
              padding: "12px 16px",
              cursor: "pointer",
              fontWeight: topMode === "vdr" ? 700 : 500,
              fontSize: 14,
            }}
          >
            📁 VDR Management
          </button>
          <button
            onClick={() => setTopMode("fdd")}
            style={{
              flex: 1,
              background: topMode === "fdd" ? "var(--theme-ink)" : "var(--theme-tint-7)",
              color: topMode === "fdd" ? "#fff" : "var(--theme-ink)",
              border: "none",
              borderLeft: "1px solid var(--theme-border-strong)",
              padding: "12px 16px",
              cursor: "pointer",
              fontWeight: topMode === "fdd" ? 700 : 500,
              fontSize: 14,
            }}
          >
            🛠️ FDD Toolbox
          </button>
          {showTax && (
            <button
              onClick={() => setTopMode("tax")}
              style={{
                flex: 1,
                background: topMode === "tax" ? "var(--theme-ink)" : "var(--theme-tint-7)",
                color: topMode === "tax" ? "#fff" : "var(--theme-ink)",
                border: "none",
                borderLeft: "1px solid var(--theme-border-strong)",
                padding: "12px 16px",
                cursor: "pointer",
                fontWeight: topMode === "tax" ? 700 : 500,
                fontSize: 14,
              }}
            >
              🧾 Tax Toolbox
            </button>
          )}
          <button
            onClick={() => setTopMode("irl")}
            style={{
              flex: 1,
              background: topMode === "irl" ? "var(--theme-ink)" : "var(--theme-tint-7)",
              color: topMode === "irl" ? "#fff" : "var(--theme-ink)",
              border: "none",
              borderLeft: "1px solid var(--theme-border-strong)",
              padding: "12px 16px",
              cursor: "pointer",
              fontWeight: topMode === "irl" ? 700 : 500,
              fontSize: 14,
            }}
          >
            📋 IRL
          </button>
          <button
            onClick={() => setTopMode("ppt")}
            style={{
              flex: 1,
              background: topMode === "ppt" ? "var(--theme-ink)" : "var(--theme-tint-7)",
              color: topMode === "ppt" ? "#fff" : "var(--theme-ink)",
              border: "none",
              borderLeft: "1px solid var(--theme-border-strong)",
              padding: "12px 16px",
              cursor: "pointer",
              fontWeight: topMode === "ppt" ? 700 : 500,
              fontSize: 14,
            }}
          >
            📊 PPT
          </button>
        </div>

        {/* VDR sub-toggle: aligned under VDR Management only (left 1/3 of the box above) */}
        {topMode === "vdr" && (
          <div style={{ display: "flex" }}>
            <div
              style={{
                flex: 1,
                display: "flex",
                gap: 0,
                borderRadius: "0 0 10px 10px",
                overflow: "hidden",
                border: "1px solid var(--theme-border-strong)",
                borderTop: "none",
                background: "var(--theme-tint-15)",
              }}
            >
              <button
                onClick={() => setVdrMode("dataroom")}
                style={{
                  flex: 1,
                  background: vdrMode === "dataroom" ? "var(--theme-accent)" : "transparent",
                  color: vdrMode === "dataroom" ? "#fff" : "var(--theme-ink)",
                  border: "none",
                  padding: "10px 12px",
                  cursor: "pointer",
                  fontWeight: vdrMode === "dataroom" ? 700 : 500,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                📄 Dataroom Upload
              </button>
              <button
                onClick={() => setVdrMode("mirroring")}
                style={{
                  flex: 1,
                  background: vdrMode === "mirroring" ? "var(--theme-accent)" : "transparent",
                  color: vdrMode === "mirroring" ? "#fff" : "var(--theme-ink)",
                  border: "none",
                  borderLeft: "1px solid var(--theme-border-strong)",
                  padding: "10px 12px",
                  cursor: "pointer",
                  fontWeight: vdrMode === "mirroring" ? 700 : 500,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                🔄 Folder Mirroring
              </button>
            </div>
            {/* spacer matches the FDD Toolbox (+ Tax Toolbox) + IRL + PPT columns above */}
            <div style={{ flex: showTax ? 4 : 3 }} />
          </div>
        )}

        {/* Dataroom Upload mode (original) */}
        {topMode === "vdr" && vdrMode === "dataroom" && <>

        {/* Index upload (REAL) */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {/* ✅ Provider picker */}
            <label style={{ fontSize: 13, opacity: 0.8 }}>VDR</label>
            <select
              value={providerId}
              onChange={(e) => setProviderId(e.target.value as VDRProviderId)}
              style={{ padding: 8, borderRadius: 8, background: "#0d1117", color: "#eee", border: "1px solid #333" }}
              aria-label="VDR provider"
            >
              {VDR_PROVIDERS.map((p) => (
                <option key={p.id} value={p.id} disabled={p.id !== "none" && !isProviderImplemented(p.id)}>
                  {p.label}{p.id !== "none" && !isProviderImplemented(p.id) ? " (coming soon)" : ""}
                </option>
              ))}
            </select>

            {/* Sync from SharePoint button */}
            <button
              onClick={handleSyncFromSharePoint}
              disabled={indexRunning || syncRunning || !project?.folder || providerId === "none"}
              title={providerId === "none"
                ? "Select a VDR provider first"
                : lastSyncDate
                ? `Last synced: ${new Date(lastSyncDate).toLocaleString()}. Click to re-sync.`
                : "Fetch the current folder structure from SharePoint for comparison"
              }
              style={{
                background: syncRunning ? "#94a3b8" : providerId === "none" ? "#6b7280" : lastSyncDate ? "var(--theme-accent)" : "#3b82f6",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: (indexRunning || syncRunning || !project?.folder || providerId === "none") ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {syncRunning
                ? "Syncing..."
                : lastSyncDate
                  ? `Synced (${new Date(lastSyncDate).toLocaleDateString()})`
                  : "Sync from SharePoint"
              }
            </button>

            <button
              onClick={() => indexInputRef.current?.click()}
              disabled={indexRunning || syncRunning}
              style={{
                background: indexRunning || syncRunning ? "var(--theme-action-disabled)" : "var(--theme-accent)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 14px",
                cursor: indexRunning || syncRunning ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              {indexRunning ? "Working…" : "📄 Create Folders from Index"}
            </button>
            <span style={{ fontSize: 12, color: "var(--theme-ink-soft)" }}>
              {"Select an index (.xlsx/.xls/.csv). Sync from SharePoint first for fastest comparison."}
            </span>
          </div>
          {lastSyncDate && (
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
              Last SharePoint sync: {new Date(lastSyncDate).toLocaleString()}
            </div>
          )}
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            ref={indexInputRef}
            onChange={handleIndexFileSelect}
            style={{ display: "none" }}
          />
        </div>

        {/* Folder creation preview/confirmation */}
        {folderPreview && (
          <div
            style={{
              marginTop: 16,
              border: "2px solid #f59e0b",
              borderRadius: 12,
              background: "#fffbeb",
              padding: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 24 }}>📊</span>
              <h3 style={{ margin: 0, color: "#92400e", fontSize: 16, fontWeight: 700 }}>
                Folder Creation Preview
              </h3>
            </div>

            <div
              style={{
                background: "#ffffff",
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
                border: "1px solid #fcd34d",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 600, color: "#92400e" }}>Total folders in VDR:</span>
                <span style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 18 }}>
                  {folderPreview.totalFolders}
                </span>
              </div>
              {folderPreview.knownFromPrevious > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, color: "#92400e" }}>Known from previous upload:</span>
                  <span style={{ fontWeight: 700, color: "#6b7280", fontSize: 18 }}>
                    {folderPreview.knownFromPrevious} <span style={{ fontSize: 12, fontWeight: 400 }}>(skipped)</span>
                  </span>
                </div>
              )}
              {folderPreview.newRows.length > 0 && (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: "#92400e" }}>New since last upload:</span>
                    <span style={{ fontWeight: 700, color: "var(--theme-ink-soft)", fontSize: 18 }}>
                      {folderPreview.newRows.length}
                    </span>
                  </div>
                  <div style={{ paddingLeft: 16, borderLeft: "2px solid #fcd34d", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontWeight: 500, color: "#78716c", fontSize: 14 }}>Will reuse (already exist):</span>
                      <span style={{ fontWeight: 600, color: "var(--theme-ink-soft)", fontSize: 16 }}>
                        {folderPreview.willReuse}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontWeight: 500, color: "#78716c", fontSize: 14 }}>Will create (new):</span>
                      <span style={{ fontWeight: 600, color: folderPreview.willCreate > 0 ? "#d68910" : "var(--theme-ink-soft)", fontSize: 16 }}>
                        {folderPreview.willCreate}
                      </span>
                    </div>
                  </div>
                </>
              )}
              {folderPreview.newRows.length === 0 && folderPreview.knownFromPrevious > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, background: "#d1fae5", padding: 8, borderRadius: 6 }}>
                  <span style={{ fontWeight: 600, color: "#065f46" }}>✅ All folders already known!</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                <span style={{ fontWeight: 600, color: "#92400e" }}>Index file:</span>
                <span style={{ color: "var(--theme-ink-soft)", fontFamily: "monospace", fontSize: 13 }}>
                  {folderPreview.file.name}
                </span>
              </div>
            </div>

            {/* Warning if VDR structure might have been reorganized */}
            {folderPreview.knownFromPrevious > 100 &&
              folderPreview.willCreate > 500 && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fca5a5",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 18 }}>⚠️</span>
                    <strong style={{ color: "#991b1b" }}>VDR Structure May Have Changed</strong>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: "#7f1d1d", lineHeight: 1.5 }}>
                    You had {folderPreview.knownFromPrevious} folders from your last upload, but this index has{" "}
                    {folderPreview.willCreate} new folders to create. The VDR may have been reorganized.
                    Consider downloading the entire VDR fresh instead of using incremental upload.
                  </p>
                </div>
              )}

            {/* Warning if creating many folders when many already exist in SharePoint */}
            {folderPreview.willReuse > 0 &&
              folderPreview.willCreate > 50 &&
              folderPreview.willCreate / folderPreview.totalFolders > 0.3 && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fca5a5",
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 18 }}>⚠️</span>
                    <strong style={{ color: "#991b1b" }}>Possible Duplicate Structure</strong>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: "#7f1d1d", lineHeight: 1.5 }}>
                    The target location already contains {folderPreview.willReuse} matching folders, but
                    you're about to create {folderPreview.willCreate} new ones. This might indicate
                    you're creating duplicate folders. Please verify this is the correct location.
                  </p>
                </div>
              )}

            {/* Folder tree visualization — Reddit-style nested thread */}
            {folderPreview.rowsToCreate.length > 0 && (
              <div style={{
                background: "#1a1f2b",
                border: "1px solid #333",
                borderRadius: 10,
                padding: 16,
                marginBottom: 12,
                maxHeight: 400,
                overflow: "auto",
              }}>
                <div style={{ fontWeight: 600, color: "#d68910", marginBottom: 10, fontSize: 13 }}>
                  New folders to create ({folderPreview.rowsToCreate.length}):
                </div>
                {(() => {
                  // Build tree from flat rows
                  const rows = [...folderPreview.rowsToCreate].sort((a, b) =>
                    a.code.localeCompare(b.code, undefined, { numeric: true })
                  );
                  // Also include existing (reused) rows as context parents
                  const allRows = [...folderPreview.allRows].sort((a, b) =>
                    a.code.localeCompare(b.code, undefined, { numeric: true })
                  );
                  const newCodes = new Set(rows.map(r => r.code));

                  // Find which parent codes are needed for context
                  const contextCodes = new Set<string>();
                  for (const row of rows) {
                    let parent = row.code;
                    while (parent.includes(".")) {
                      parent = parent.slice(0, parent.lastIndexOf("."));
                      if (!newCodes.has(parent)) contextCodes.add(parent);
                    }
                  }

                  // Render rows with indentation
                  const visibleRows = allRows.filter(r => newCodes.has(r.code) || contextCodes.has(r.code));
                  return visibleRows.map((row) => {
                    const depth = row.code.split(".").length - 1;
                    const isNew = newCodes.has(row.code);
                    return (
                      <div
                        key={row.code}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          paddingLeft: depth * 20,
                          marginBottom: 2,
                          fontSize: 12,
                          color: isNew ? "#fbbf24" : "#6b7280",
                          fontFamily: "monospace",
                        }}
                      >
                        {/* Thread line */}
                        {depth > 0 && (
                          <span style={{ color: "#444", userSelect: "none" }}>
                            {"│ ".repeat(depth - 1)}├─
                          </span>
                        )}
                        <span style={{
                          fontWeight: isNew ? 600 : 400,
                          fontStyle: isNew ? "normal" : "italic",
                        }}>
                          {providerId === "requestlist"
                            ? String(row.meta?.folderName ?? row.title)
                            : providerId === "firmex" && depth === 0 ? row.title : `${row.code} ${row.title}`}
                        </span>
                        {isNew && (
                          <span style={{
                            background: "#92400e",
                            color: "#fef3c7",
                            padding: "1px 6px",
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            fontFamily: "system-ui",
                          }}>
                            NEW
                          </span>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={confirmAndCreateFolders}
                disabled={indexRunning}
                style={{
                  background: "var(--theme-action)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 16px",
                  cursor: indexRunning ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  flex: 1,
                }}
              >
                ✅ Proceed with Folder Creation
              </button>
              <button
                onClick={cancelFolderCreation}
                disabled={indexRunning}
                style={{
                  background: "#dc2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 16px",
                  cursor: indexRunning ? "not-allowed" : "pointer",
                  fontWeight: 700,
                  flex: 1,
                }}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        )}

        {/* Drag area */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          style={{
            marginTop: 18,
            padding: 36,
            border: "2px dashed var(--theme-accent)",
            background: "var(--theme-tint-7)",
            color: "var(--theme-ink)",
            borderRadius: 12,
            textAlign: "center",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            Drag files here
          </div>
          <div style={{ fontSize: 12, color: "var(--theme-ink-soft)", marginBottom: 14 }}>
            {hasIndex
              ? "or choose files manually"
              : "or choose files manually"}
          </div>

          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              background: "var(--theme-accent)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 14px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            📁 Select Files
          </button>

          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>

        {/* File preview / progress list — hidden while folder creation preview is pending */}
        {files.length > 0 && !uploading && !folderPreview && (
          <FilePreviewTable
            files={files}
            selectedFiles={selectedFiles}
            onToggleFile={toggleFileSelection}
            onToggleAll={toggleAllFiles}
            onDeselectFiles={(names) => setSelectedFiles((prev) => {
              const next = new Set(prev);
              names.forEach(n => next.delete(n));
              return next;
            })}
            rootItemId={project?.folder?.folderId || ""}
            indexMap={project?.id ? (getIndexMap(project.id) || {}) : {}}
            providerId={providerId}
            indexRows={project?.id ? (getIndexRows(project.id) || []) : []}
            projectId={project.id}
          />
        )}

        {/* Upload progress list (shown during upload) */}
        {files.length > 0 && uploading && (
          <div
            style={{
              marginTop: 16,
              border: "1px solid var(--theme-border-strong)",
              borderRadius: 10,
              background: "var(--theme-tint-15)",
              padding: 12,
            }}
          >
            <div style={{ fontWeight: 700, color: "var(--theme-ink)", marginBottom: 6 }}>Upload Progress</div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {items.map((it) => (
                <li
                  key={it.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "8px 0",
                    borderBottom: "1px solid var(--theme-tint-24)",
                    fontSize: 12,
                  }}
                >
                  <div style={{ flex: 1 }}>{it.name}</div>
                  <div style={{ width: 140, textTransform: "capitalize" }}>{it.status}</div>
                  <div style={{ width: 140 }}>
                    {typeof it.progress === "number" && (
                      <div
                        style={{
                          height: 6,
                          borderRadius: 6,
                          background: "var(--theme-tint-24)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${it.progress}%`,
                            height: "100%",
                            background: "var(--theme-accent)",
                            transition: "width .2s",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div style={{ width: 160, color: "var(--theme-ink)" }}>{it.note}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Primary actions */}
        <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
          <button
            onClick={uploadFiles}
            disabled={uploading || files.length === 0 || !hasIndex}
            title={!hasIndex ? "Upload a file index first before uploading documents." : ""}
            style={{
              background: uploading || !hasIndex ? "var(--theme-action-disabled)" : "var(--theme-accent)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 16px",
              cursor: uploading || files.length === 0 || !hasIndex ? "not-allowed" : "pointer",
              fontWeight: 700,
              minWidth: 180,
            }}
          >
            {uploading ? "Uploading…" : "Upload Files"}
          </button>

          <button
            onClick={async () => {
              if (!project?.folder?.driveId || !project.folder?.folderId) {
                setMessages((prev) => [...prev, "❌ Please select a folder first."]);
                return;
              }
              if (files.length === 0) {
                setMessages((prev) => [...prev, "❌ No files selected."]);
                return;
              }

              const driveId = project.folder.driveId;
              const rootItemId = project.folder.folderId;
              const folderLink = project.folder.webUrl || "";
              const indexMap = project?.id ? (getIndexMap(project.id) || {}) : {};

              if (!indexMap || Object.keys(indexMap).length === 0) {
                setMessages((prev) => [...prev, "❌ No index map found. Create folders from index first."]);
                return;
              }

              // Get index rows for provider-specific routing (e.g., Firmex)
              const indexRows = project?.id ? (getIndexRows(project.id) || []) : [];

              // Filter to only selected files
              const filesToUpload = files.filter(f => selectedFiles.has(f.name));
              if (filesToUpload.length === 0) {
                setMessages((prev) => [...prev, "❌ No files selected for upload."]);
                return;
              }

              setUploading(true);

              // Files were already unzipped when added (drop/picker), so don't unzip
              // again here — a second pass would re-open nested ZIP documents. Unzip once.
              const expandedFiles = filesToUpload;

              setMessages((prev) => [
                ...prev,
                `🚀 Uploading ${expandedFiles.length} file(s) and will send email after completion…`,
              ]);

              try {
                // run the normal upload router (resolves missing parents on-demand)
                const results = await routeAndUploadFiles({
                  driveId,
                  rootItemId,
                  files: expandedFiles,
                  indexMap,
                  providerId,
                  indexRows,
                  onProgress: (file, p) => {
                    setItems((prev) =>
                      prev.map((it) =>
                        it.name === file.name ? { ...it, progress: Math.round(p * 100) } : it
                      )
                    );
                  },
                  onStatus: (file, status, note) => {
                    setItems((prev) =>
                      prev.map((it) =>
                        it.name === file.name
                          ? {
                              ...it,
                              status,
                              note: note ?? it.note,
                              progress:
                                status === "uploading"
                                  ? it.progress ?? 0
                                  : status === "done"
                                  ? 100
                                  : it.progress,
                            }
                          : it
                      )
                    );
                  },
                });

                // Show the SAME summary as the "Upload Files" button (status-only)
                const ok = (results as any[]).filter((r: any) => r.status === "done").length;
                const skipped = (results as any[]).filter((r: any) => r.status === "skipped").length;
                const bad = (results as any[]).filter((r: any) => r.status === "error").length;

                setMessages((prev) => [
                  ...prev,
                  `✅ Finished uploading. Success: ${ok} • Skipped: ${skipped} • Failed: ${bad}`,
                ]);

                // Flag failed files for ZIP download
                const failedEmailFiles = expandedFiles.filter(f =>
                  results.some((r: any) => r.fileName === f.name && r.status === "error")
                );
                if (failedEmailFiles.length > 0) {
                  setFlaggedFiles((prev) => [...prev, ...failedEmailFiles]);
                }

                // Save any newly discovered folder IDs
                if (project?.id) {
                  saveIndexMap(project.id, indexMap);
                }

                // ✅ Build grouped summary for EMAIL from successful uploads ONLY
                // Group by final folder path, link to final folder web URL
                const grouped: Record<string, { name: string; url?: string; status?: string }[]> = {};
                const seenByFolder = new Map<string, Set<string>>();

                for (const r of results as any[]) {
                  const s = String(r?.status || "").toLowerCase();

                  // Only include actual uploads in the email
                  if (s !== "done") continue;

                  const folderPath: string = r?.parentPath || r?.parentCode || "root";
                  const url: string = r?.webUrl || "";  // Use actual file URL, not parent
                  const displayName: string = r?.fileName || "file";

                  if (!grouped[folderPath]) {
                    grouped[folderPath] = [];
                    seenByFolder.set(folderPath, new Set());
                  }
                  const seen = seenByFolder.get(folderPath)!;
                  const dedupeKey = `${displayName}||${url}`;
                  if (!seen.has(dedupeKey)) {
                    grouped[folderPath].push({ name: displayName, url, status: s });
                    seen.add(dedupeKey);
                  }
                }

                if (Object.keys(grouped).length === 0) {
                  setMessages((prev) => [...prev, "ℹ️ No items to include in email (no links found)."]);
                } else {
                  await sendUploadSummaryEmail({
                    grouped,
                    folderLink,
                    title: `New Uploads – ${project.name}`,
                    indexRows,
                  });
                  setMessages((prev) => [...prev, "✅ Upload and email completed successfully."]);
                }
              } catch (err: any) {
                console.error("Upload & Email failed:", err);
                setMessages((prev) => [
                  ...prev,
                  `💥 Upload or email failed: ${err?.message || String(err)}`,
                ]);
              } finally {
                setUploading(false);
              }
            }}
            disabled={uploading || files.length === 0}
            style={{
              background: uploading ? "var(--theme-action-disabled)" : "var(--theme-action)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 16px",
              cursor: uploading || files.length === 0 ? "not-allowed" : "pointer",
              fontWeight: 700,
              minWidth: 180,
            }}
          >
            {uploading ? "Uploading…" : "Upload & Email"}
          </button>

          <button
            onClick={downloadDeselected}
            disabled={uploading || files.length === 0 || selectedFiles.size === files.length}
            style={{
              background: selectedFiles.size === files.length ? "#ccc" : "#d68910",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 16px",
              cursor:
                uploading || files.length === 0 || selectedFiles.size === files.length
                  ? "not-allowed"
                  : "pointer",
              fontWeight: 700,
              minWidth: 180,
            }}
            title={
              selectedFiles.size === files.length
                ? "No files deselected"
                : `Download ${files.length - selectedFiles.size} deselected file(s)`
            }
          >
            📥 Download Deselected ({files.length - selectedFiles.size})
          </button>

          <button
            onClick={() => {
              setFiles([]);
              setItems([]);
              setFlaggedFiles([]);
              setMessages((prev) => [...prev, "🧹 Cleared file list."]);
            }}
            style={{
              background: "#e0e0e0",
              color: "#333",
              border: "none",
              borderRadius: 8,
              padding: "10px 16px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Clear
          </button>
        </div>

        {/* Flagged files banner */}
        {flaggedFiles.length > 0 && (
          <div
            style={{
              marginTop: 16,
              border: "1px solid #e6a817",
              background: "#fef9e7",
              borderRadius: 10,
              padding: 14,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <strong style={{ color: "#b7950b" }}>
                  {flaggedFiles.length} file(s) could not be routed
                </strong>
                <div style={{ fontSize: 12, color: "#7d6608", marginTop: 4 }}>
                  No matching parent folder was found in the index map. Download these files to handle manually.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={async () => {
                    await downloadFilesAsZip(flaggedFiles, `flagged-files-${new Date().toISOString().slice(0, 10)}.zip`);
                    setMessages((prev) => [...prev, `📥 Downloaded ${flaggedFiles.length} flagged file(s) as ZIP.`]);
                  }}
                  style={{
                    background: "#d68910",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 14px",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  📥 Download as ZIP
                </button>
                <button
                  onClick={() => setFlaggedFiles([])}
                  style={{
                    background: "#e0e0e0",
                    color: "#333",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 14px",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
            <div style={{ marginTop: 8, maxHeight: 120, overflowY: "auto", fontSize: 12, color: "#7d6608" }}>
              {flaggedFiles.map((f, i) => (
                <div key={i}>{f.name}</div>
              ))}
            </div>
          </div>
        )}

        </>}
        {/* end Dataroom Upload mode */}

        {/* Folder Mirroring mode */}
        {topMode === "vdr" && vdrMode === "mirroring" && project?.folder?.driveId && project?.folder?.folderId && (
          <FolderMirroring
            driveId={project.folder.driveId}
            rootItemId={project.folder.folderId}
            rootFolderName={project.folder.displayPath || "selected folder"}
            projectName={project.name}
            projectId={project.id}
          />
        )}

        {/* FDD Toolbox mode */}
        {topMode === "fdd" && (
          <FDDToolbox
            projectId={project.id}
            folderSelected={folderSelected}
            projectName={project.name}
          />
        )}

        {/* Tax Toolbox mode */}
        {topMode === "tax" && showTax && (
          <TaxToolbox
            projectId={project.id}
            folderSelected={folderSelected}
            projectName={project.name}
          />
        )}

        {/* IRL mode */}
        {topMode === "irl" && <IRLToolbox projectId={project.id} />}

        {topMode === "ppt" && <PPTToolbox projectId={project.id} />}

        {/* Messages */}
        {messages.length > 0 && (
          <div style={{ marginTop: 16 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ fontSize: 12, color: "var(--theme-ink)", marginBottom: 4 }}>
                {m}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default DragAndDrop;
